import axios, { AxiosResponse } from "axios";
import { v4 as uuidv4 } from "uuid";
import { IEventLogFilters } from "../components/EventLog/Filters/types";
import { getUrlName } from "../lib/Util";
import { requireToken } from "../strict/lib/authErrors";

const request = () =>
  axios.create({
    headers: {
      "Request-ID": uuidv4(),
    },
  });

const heartBeat = async () => {
  return await request().get("/_heartbeat", { timeout: 3000 });
};

const downloadFileApiCall = (flow_source_id: string | number, accessToken: string, signal?: AbortSignal) => {
  return request()({
    url: `/api/${getUrlName()}/metrics/sample/flow_sources/download/${flow_source_id}`,
    method: "GET",
    responseType: "blob",
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
};

interface CredentialsType {
  email: string;
  password: string;
}

const getUserInformation = async (currentUrlName: string | null, accessToken: string) => {
  const loginUrl = currentUrlName ? `/api/user/login?url_name=${currentUrlName}` : "/api/user/login";

  return request().get(loginUrl, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const parseFilters = (filters: DownloadFilters | UploadsFilters | IEventLogFilters) =>
  Object.entries(filters).reduce(
    (acc, [key, value]) => {
      if (value && key !== "first") {
        acc[key] = String(value);
      }
      return acc;
    },
    {} as Record<string, string>
  );

const handleExportAsCsv = async (
  filters: DownloadFilters | UploadsFilters | IEventLogFilters,
  getAccessTokenSilently: () => Promise<string | undefined>,
  type: "downloads" | "uploads" | "events",
  signal?: AbortSignal
): Promise<AxiosResponse> => {
  const queryParams = new URLSearchParams({
    ...parseFilters(filters),
    type,
  });
  const accessToken = requireToken(await getAccessTokenSilently());

  const url = `/api/${getUrlName()}/exportAsCsv`;

  const response = await request().get(url, {
    params: queryParams,
    headers: { Authorization: `Bearer ${accessToken}` },
    signal,
  });
  return response;
};

const downloadDirtyRows = async (
  downloadPartId: string,
  getAccessTokenSilently: () => Promise<string | undefined>,
  newMethod?: boolean
) => {
  const accessToken = requireToken(await getAccessTokenSilently());

  const downloadLink = `/api/${getUrlName()}/files/${downloadPartId}${
    newMethod ? "_new_dirtyrows.json.gz" : "_dirtyrows.csv.gz"
  }`;

  const response = await request().get(downloadLink, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response;
};

const getFirstNDirtyRows = async (
  downloadPartId: string,
  getAccessTokenSilently: () => Promise<string | undefined>,
  numberOfDirtyRows: number
) => {
  const accessToken = requireToken(await getAccessTokenSilently());

  const downloadLink = `/api/${getUrlName()}/files/${downloadPartId}_first_${numberOfDirtyRows}_rows`;

  const response = await request().get(downloadLink, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response;
};

export {
  CredentialsType,
  downloadDirtyRows,
  downloadFileApiCall,
  getFirstNDirtyRows,
  getUserInformation,
  handleExportAsCsv,
  heartBeat,
};
